import { useContext, useState, useEffect, useCallback } from "react";
// Removed icons that were only used in getData: MapPinIcon, PencilSquareIcon, InformationCircleIcon, CreditCardIcon
import { HomeIcon } from "@heroicons/react/24/outline"; // HomeIcon is still used in ChatContext INITIAL_STATE and RESET_ACCOUNT_VIEW
import { ChatContext } from "../context/ChatContext.jsx";  
import AsyncLocalStorage from '@createnextapp/async-local-storage'; 
import Dashboard from "./Dashboard.jsx";
import { v4 as uuidv4 } from "uuid";
import socket from "../services/socket.js"; 

const Home = () => {

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);  
  // const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Unused
  const { dispatch, data } = useContext(ChatContext);  
  // const [accountNo, setAccountNo] = useState(data.accountNo); // Prefer using data.accountNo directly from context
  // const [currentChat, setCurrentChat] = useState(data.currentChat); // Prefer using data.currentChat directly from context 
  // const [name, setName] = useState(""); // name is set in context via SET_USER
  // const [forceHomeRenderVersion, setForceHomeRenderVersion] = useState(0); // Unused
 
  useEffect(() => {      
    initializeConnection();

    return () => {
      // Cleanup socket listeners
      socket.off("last-messages");
      socket.off("connect_error");
      socket.off("active-chats");  
      socket.off("connect");  
      socket.off("reconnect_attempt");  
    }; 
  }, []); 

    const initializeConnection = async () => {
    try { 

      let localClientId = await AsyncLocalStorage.getItem("silu-ClientId");
      if (!localClientId) {
        localClientId = uuidv4();
        await AsyncLocalStorage.setItem("silu-ClientId", localClientId);
      } 
      
      let userArray;
      try {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const userParam = urlParams.get("u");
        if (!userParam) throw new Error("User parameter 'u' is missing from URL");
        userArray = atob(userParam).split("_");
        if (userArray.length < 2) throw new Error("User parameter 'u' is malformed"); 
        // setName(userArray[1]); // Name is set in context via SET_USER
        dispatch({ type: "SET_USER", payload: {"id": userArray[0], "name": userArray[1] } });
      } catch (e) {
        console.error("Failed to parse user from URL params:", e.message);
        setError(`Invalid user data in URL: ${e.message}`);
        setIsLoading(false);
        return;
      }

      setError(null); 
      setIsLoading(true); 

      socket.off("last-messages").on("last-messages", (messages) => {     
        //console.log("Received 'last-messages'");
        dispatch({ type: "SET_LAST_MESSAGES", payload: messages });  
        setIsLoading(false);
      });

      socket.off("active-chats").on("active-chats", (messages) => {     
            // console.log("Received 'active-chats' payload:", messages.map(p => p.channel_id));
            // console.log("Received 'active-chats' length:", messages?.length);
        dispatch({ type: "SET_ACTIVE_CHATS", payload: messages });  
        setIsLoading(false);
      }); 

      socket.off("connect_error").on("connect_error", (err) => {
        //console.warn("Socket connection error. Client will attempt to reconnect automatically:", err.message);
        setError(`Connection error: ${err.message}. Attempting to reconnect...`); 
        setIsLoading(true);  
      });

      socket.off("reconnect_attempt").on("reconnect_attempt", (attemptNumber) => {
        //console.log(`Socket reconnect attempt #${attemptNumber}`);
        // You could update the error message here to include the attempt number if desired:
        // setError(`Connection lost. Reconnecting (attempt ${attemptNumber})...`);
      });


      const performJoin = () => {
        const channelToJoin = data.currentChat?.channel_id;
        const userName = userArray?.[1]; 

        if (channelToJoin != null && localClientId && userName) {  
          //console.log(`Emitting 'join' for channel: ${channelToJoin}, clientId: ${localClientId}, userName: ${userName}`);
          socket.emit("join", { clientId: localClientId, userName: userName, department: 1, direction: 1, channel: channelToJoin, client: 'agent' });
        } else {
          //console.warn("Cannot join channel. Missing critical info:", { channelToJoin, localClientId, userName });
          setIsLoading(false);  
        }
      };

      if (socket.connected) {
        //console.log("Socket already connected. Performing join.");
        performJoin();
      } else {
        //console.log("Socket not connected. Setting up 'connect' listener then performing join.");
        socket.off("connect").on("connect", () => { 
          console.log("Socket connected via listener. Performing join.");
          setError(null); 
          performJoin();
        });
        socket.connect();
      }
 
    } catch (err) {
      console.error("Error during connection initialization:", err);
      setError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  // getData and its related useEffect have been moved to MessageScreen.jsx
  
  useEffect(() => {
    // This useEffect is now primarily for observing context changes if needed for Home.jsx itself,
    // or can be removed if Home.jsx no longer directly reacts to these specific data changes.
    // console.log(`Home.jsx Effect (Post-Move): accountNo=${data.accountNo}, isAccountDataLoading=${data.isAccountDataLoading}, userName=${data.user?.name}`);
  }, [data.accountNo, data.user?.name, data.isAccountDataLoading, dispatch]);

  const handleRetry = async () => {
    setIsRetrying(true);
    await initializeConnection();
    setIsRetrying(false);
  }; 

 
  if (isLoading && !error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div> 
      </div>
    );
  }


  if (error) {
    return (
      <div className="max-w-md mx-auto mt-8 bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-gray-900">Connection Error</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <div className="mt-6 space-x-3">
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isRetrying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Retrying...
                </>
              ) : (
                'Try Again'
              )}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full ">  
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden"> 
        <Dashboard
          // Pass critical context values as props to ensure Dashboard uses the latest
          channelIdFromHome={data.channelId}
          accountDataVersionFromHome={data._accountDataVersion}
        />
      </div>
    </div>
  );
};

export default Home;
